import { useState } from "react";
import { useForm } from "react-hook-form";
import { passwordSchema, confirmPasswordSchema } from "../utils/validationSchemas";
import Breadcrumb from "../components/Breadcrumb";
import AuthService from "../services/LoginService";
import StorageService from "../services/StorageService";

export default function ChangePassword() {
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  const onSubmit = async (data) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (data.oldPassword === data.newPassword) {
      setErrorMessage("New password cannot be same as old password!");
      return;
    }

    if (data.newPassword !== data.confirmPassword) {
      setErrorMessage("Passwords do not match!");
      return;
    }

    try {
      setLoading(true);
      const employeeId = StorageService.loginData().employeeId;

      const res = await AuthService.changePassword({
        employeeId,
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      });

      if (res.status) {
        setSuccessMessage("Password changed successfully!");
        setTimeout(() => setSuccessMessage(""), 2000);
        document.querySelector("form").reset();
      } else {
        setErrorMessage(res.message || "Failed to change password!");
      }
    } catch (error) {
      setErrorMessage(error.message || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Breadcrumb pageTitle="Change Password" secondTitle="Profile" />
      <div className="row">
        <div className="card p-4 bg-white shadow-sm">
          {/* Alerts */}
          {errorMessage && (
            <div className="alert alert-danger d-flex justify-content-between align-items-center mb-3 p-2">
              <span>{errorMessage}</span>
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
              />
            </div>
          )}
          {successMessage && (
            <div className="alert alert-success d-flex justify-content-between align-items-center mb-3 p-2">
              <span>{successMessage}</span>
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage("")}
              />
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Old Password */}
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">
                  Old Password <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <input
                    placeholder="Enter old password"
                    type={showOldPassword ? "text" : "password"}
                    {...register("oldPassword", passwordSchema)}
                    className={`form-control ${errors.oldPassword ? "is-invalid" : ""
                      }`}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowOldPassword(!showOldPassword)}
                  >
                    <i
                      className={showOldPassword ? "ti ti-eye" : "ti ti-eye-off"}
                    ></i>
                  </button>
                  <div className="invalid-feedback">
                    {errors.oldPassword?.message}
                  </div>
                </div>
              </div>
            </div>

            {/* New Password */}
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">
                  New Password <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <input
                    placeholder="Enter new password"
                    type={showNewPassword ? "text" : "password"}
                    {...register("newPassword", passwordSchema)}
                    className={`form-control ${errors.newPassword ? "is-invalid" : ""
                      }`}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    <i
                      className={showNewPassword ? "ti ti-eye" : "ti ti-eye-off"}
                    ></i>
                  </button>
                  <div className="invalid-feedback">
                    {errors.newPassword?.message}
                  </div>
                </div>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">
                  Confirm Password <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <input
                    placeholder="Re-enter new password"
                    type={showConfirmPassword ? "text" : "password"}
                    {...register("confirmPassword", {
                        ...confirmPasswordSchema,
                        validate: (value) =>
                          value === watch("newPassword") ||
                          "Passwords do not match",
                      })}
                    className={`form-control ${errors.confirmPassword ? "is-invalid" : ""
                      }`}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() =>
                      setShowConfirmPassword(!showConfirmPassword)
                    }
                  >
                    <i
                      className={
                        showConfirmPassword ? "ti ti-eye" : "ti ti-eye-off"
                      }
                    ></i>
                  </button>
                  <div className="invalid-feedback">
                    {errors.confirmPassword?.message}
                  </div>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div>
              <button
                type="submit"
                className="btn btn-primary me-2"
                disabled={loading}
              >
                <i className="ti ti-circle-check me-1"></i>{" "}
                {loading ? "Updating..." : "Update"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => window.history.back()}
                disabled={loading}
              >
                <i className="ti ti-cancel me-1"></i> Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
